import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchPendingAlerts, fetchAgentPostSaleAlerts, type PendingAlerts } from '@/services/alerts';
import { getAgentSalespersonId } from '@/services/agent';
import { useAuth } from '@/context/AuthContext';

interface AgentPostSale {
  warranties: number;
  returns: number;
  exchanges: number;
}

// Panel de avisos/alertas. Para el admin: "falta pagar X de comisión a
// [vendedor]" y "falta entregar X de donación a [entidad]". Para el agente de
// ventas: garantías, devoluciones y cambios pendientes a su nombre.
export default function PendingAlerts() {
  const { profile } = useAuth();
  const role = profile?.role ?? '';
  const isAgent = role === 'sales_agent';

  const [alerts, setAlerts] = useState<PendingAlerts | null>(null);
  const [postSale, setPostSale] = useState<AgentPostSale | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    if (isAgent) {
      getAgentSalespersonId().then((spId) => {
        if (!spId) {
          if (!cancelled) setLoading(false);
          return;
        }
        fetchAgentPostSaleAlerts(spId).then((p) => {
          if (!cancelled) setPostSale(p);
          if (!cancelled) setLoading(false);
        });
      });
    } else {
      fetchPendingAlerts().then((a) => {
        if (!cancelled) setAlerts(a);
        if (!cancelled) setLoading(false);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [isAgent]);

  if (loading) return null;

  // --- Vista del agente: alertas de post-venta a su nombre ---
  if (isAgent) {
    if (!postSale) return null;
    const total = postSale.warranties + postSale.returns + postSale.exchanges;
    if (total === 0) return null;

    return (
      <div className="space-y-2">
        {postSale.warranties > 0 && (
          <Link
            to="/admin/garantias"
            className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 hover:bg-amber-100 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-shield-check-line text-amber-600"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-foreground-900 font-semibold">
                Tienes {postSale.warranties} garantía(s) a tu nombre
              </p>
              <p className="text-xs text-foreground-500 mt-0.5">Reclamos pendientes de revisión</p>
            </div>
            <i className="ri-arrow-right-s-line text-amber-600 flex-shrink-0 mt-0.5"></i>
          </Link>
        )}

        {postSale.returns > 0 && (
          <Link
            to="/admin/devoluciones"
            className="flex items-start gap-3 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 hover:bg-rose-100 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-arrow-go-back-line text-rose-600"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-foreground-900 font-semibold">
                Tienes {postSale.returns} devolución(es) a tu nombre
              </p>
              <p className="text-xs text-foreground-500 mt-0.5">Pendientes de gestión</p>
            </div>
            <i className="ri-arrow-right-s-line text-rose-600 flex-shrink-0 mt-0.5"></i>
          </Link>
        )}

        {postSale.exchanges > 0 && (
          <Link
            to="/admin/cambios"
            className="flex items-start gap-3 bg-cyan-50 border border-cyan-200 rounded-xl px-4 py-3 hover:bg-cyan-100 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-arrow-left-right-line text-cyan-600"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-foreground-900 font-semibold">
                Tienes {postSale.exchanges} cambio(s) a tu nombre
              </p>
              <p className="text-xs text-foreground-500 mt-0.5">Pendientes de gestión</p>
            </div>
            <i className="ri-arrow-right-s-line text-cyan-600 flex-shrink-0 mt-0.5"></i>
          </Link>
        )}
      </div>
    );
  }

  // --- Vista del admin: pagos / donaciones pendientes ---
  const hasCommissions = (alerts?.commissions.length ?? 0) > 0;
  const hasDonations = (alerts?.donations.length ?? 0) > 0;
  if (!alerts || (!hasCommissions && !hasDonations)) return null;

  return (
    <div className="space-y-2">
      {alerts.commissions.slice(0, 3).map((c) => (
        <Link
          key={c.salespersonId || 'none'}
          to="/admin/comisiones"
          className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 hover:bg-amber-100 transition-colors"
        >
          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-percent-line text-amber-600"></i>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-foreground-900">
              <span className="font-semibold">Falta pagar ${c.pendingAmount.toLocaleString()}</span>{' '}
              de comisión a <span className="font-medium">{c.salespersonName || 'Sin vendedor'}</span>
            </p>
            <p className="text-xs text-foreground-500 mt-0.5">{c.commissionCount} comisión(es) pendiente(s)</p>
          </div>
          <i className="ri-arrow-right-s-line text-amber-600 flex-shrink-0 mt-0.5"></i>
        </Link>
      ))}

      {alerts.donations.slice(0, 3).map((d) => (
        <Link
          key={d.donationId}
          to="/admin/donaciones"
          className="flex items-start gap-3 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 hover:bg-rose-100 transition-colors"
        >
          <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-heart-line text-rose-600"></i>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-foreground-900">
              <span className="font-semibold">Falta entregar ${d.pendingAmount.toLocaleString()}</span>{' '}
              de donación a <span className="font-medium">{d.beneficiary}</span>
            </p>
            <p className="text-xs text-foreground-500 mt-0.5">Pendiente de entrega</p>
          </div>
          <i className="ri-arrow-right-s-line text-rose-600 flex-shrink-0 mt-0.5"></i>
        </Link>
      ))}
    </div>
  );
}